import React, { useMemo, useState } from "react";
import {
  CREDENTIAL_CATEGORY_LABELS,
  CREDENTIAL_STATUS_FILTERS,
} from "@toolkit/mobile-shared";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import { ClickableTableRow } from "../../../components/dashboard/ClickableTableRow";
import {
  ProductListEmptyFiltersMessage,
  ProductListFilterField,
  ProductListToolbar,
  PRODUCT_LIST_SEARCH_PLACEHOLDER,
} from "../../../components/dashboard/ProductListToolbar";
import { ButtonLink } from "../../../components/ui/button";
import { Card, CardHeader, CardTitle } from "../../../components/ui/card";
import { SelectInput } from "../../../components/ui/FormControls";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { dashboardCredentialHref } from "../../../lib/dashboardCredentialHref";
import { ssrFormattedDateOr } from "../../../lib/ssrFormattedDate";
import type { CredentialListItem, MainAppPageProps } from "../types";
import { CredentialStatusBadge } from "./CredentialStatusBadge";

type CredentialsListPageProps = Pick<MainAppPageProps, "routes"> & {
  credentials?: CredentialListItem[];
  showTeamColumns?: boolean;
};

const STATUS_FILTERS = CREDENTIAL_STATUS_FILTERS;

export const CredentialsListPage: React.FC<CredentialsListPageProps> = ({
  routes,
  credentials = [],
  showTeamColumns = false,
}) => {
  const newPath = routes.newDashboardCredential ?? "/dashboard/credentials/new";
  const exportPath =
    routes.dashboardCredentialsExport ?? "/dashboard/credentials/export";
  const [query, setQuery] = useState("");
  const [status, setStatus] =
    useState<(typeof STATUS_FILTERS)[number]["id"]>("all");
  const [category, setCategory] = useState("all");
  const [holderId, setHolderId] = useState("all");

  const holders = useMemo(() => {
    const map = new Map<string, string>();
    credentials.forEach((item) => {
      if (!item.holderId) return;
      map.set(item.holderId, item.holderName || "Teammate");
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [credentials]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return credentials.filter((item) => {
      if (status !== "all" && item.status !== status) return false;
      if (category !== "all" && item.category !== category) return false;
      if (holderId !== "all" && item.holderId !== holderId) return false;
      if (!q) return true;
      return [item.name, item.issuer, item.holderName]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [category, credentials, holderId, query, status]);

  return (
    <DashboardPageLayout
      title="Credentials"
      headerActions={
        <div className="flex flex-wrap gap-2">
          <ButtonLink to={exportPath} variant="outline">
            Export CSV
          </ButtonLink>
          <ButtonLink to={newPath} variant="premium">
            Add a credential
          </ButtonLink>
        </div>
      }
    >
      {credentials.length === 0 ? (
        <Card className="bg-card">
          <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-base">No credentials yet</CardTitle>
            <ButtonLink to={newPath} variant="premium">
              Add a credential
            </ButtonLink>
          </CardHeader>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          <ProductListToolbar
            search={{
              value: query,
              onChange: setQuery,
              placeholder: PRODUCT_LIST_SEARCH_PLACEHOLDER.credentials,
              ariaLabel: "Search credentials",
            }}
            filters={
              <>
                <ProductListFilterField
                  id="credential-status-filter"
                  label="Status"
                >
                  <SelectInput
                    id="credential-status-filter"
                    compact
                    className="h-9 min-w-[10rem]"
                    value={status}
                    onChange={(event) =>
                      setStatus(
                        event.target
                          .value as (typeof STATUS_FILTERS)[number]["id"],
                      )
                    }
                    aria-label="Filter by status"
                  >
                    {STATUS_FILTERS.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                      </option>
                    ))}
                  </SelectInput>
                </ProductListFilterField>
                <ProductListFilterField
                  id="credential-category-filter"
                  label="Type"
                >
                  <SelectInput
                    id="credential-category-filter"
                    compact
                    className="h-9 min-w-[10rem]"
                    value={category}
                    onChange={(event) => setCategory(event.target.value)}
                    aria-label="Filter by type"
                  >
                    <option value="all">All types</option>
                    {Object.entries(CREDENTIAL_CATEGORY_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </SelectInput>
                </ProductListFilterField>
                {showTeamColumns && holders.length > 0 ? (
                  <ProductListFilterField
                    id="credential-person-filter"
                    label="Person"
                  >
                    <SelectInput
                      id="credential-person-filter"
                      compact
                      className="h-9 min-w-[10rem]"
                      value={holderId}
                      onChange={(event) => setHolderId(event.target.value)}
                      aria-label="Filter by person"
                    >
                      <option value="all">All people</option>
                      {holders.map(([id, name]) => (
                        <option key={id} value={id}>
                          {name}
                        </option>
                      ))}
                    </SelectInput>
                  </ProductListFilterField>
                ) : null}
              </>
            }
          />
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {showTeamColumns ? <TableHead>Holder</TableHead> : null}
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Expiry</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((item) => (
                  <ClickableTableRow
                    key={item.id}
                    href={dashboardCredentialHref(routes, item.id)}
                    ariaLabel={`Open credential ${item.name}`}
                  >
                    {showTeamColumns ? (
                      <TableCell className="text-muted-foreground">
                        {item.holderName || "Teammate"}
                      </TableCell>
                    ) : null}
                    <TableCell className="font-semibold text-foreground">
                      {item.name}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {CREDENTIAL_CATEGORY_LABELS[
                        item.category as keyof typeof CREDENTIAL_CATEGORY_LABELS
                      ] || item.category}
                    </TableCell>
                    <TableCell>
                      <CredentialStatusBadge status={item.status} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {item.expiresOnDisplay
                        ? ssrFormattedDateOr(item.expiresOnDisplay)
                        : "No expiry date"}
                    </TableCell>
                  </ClickableTableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {filtered.length === 0 ? <ProductListEmptyFiltersMessage /> : null}
        </div>
      )}
    </DashboardPageLayout>
  );
};
